import { useEffect, useState } from 'react';
import { Button, Input, Modal, Switch } from 'components-antd';
import styles from './styles.module.scss';
import { useFormik } from 'formik';
import classNames from 'classnames';
import { CrossIcon } from '../Icons/CrossIcon';
import { Autocomplete } from '../Autocomplete';
import { showErrorMessage, showSuccessMessage } from 'helpers';
import { Upload } from 'antd';
import { postAgentIntel } from 'api/intel/agent';
import { Spinner } from 'components';
import { v4 as uuid } from 'uuid';
import { Select } from 'antd';
import { Locations } from 'components';
import { ServiceIcons, ServiceIconTypes } from 'pages/Services/icons';
import { uniqBy } from 'lodash-es';
import { sortLocations } from 'helpers';
import { getYouTubeThumbnail, isValidURL } from 'utils';
import {
  CheckmarkIcon,
  DraftsIcon,
  FrameIcon,
  SearchIcon,
  TagsIcon,
  UsersIcon,
  UploadIcon,
} from '../Icons';

export interface ShareIntelModalProps {
  open: boolean;
  onClose: () => void;
  systemTags: any[];
  allClients: any[];
  loading: boolean;
  handleNewPost: Function;
}

enum Editor {
  Text = 'text',
  Photo = 'photo',
  Tags = 'tags',
  PublishTo = 'publishTo',
}

enum PublishTo {
  All = 'All',
  Clients = 'Clients',
  Locations = 'Locations',
}

type TagStateType = {
  value: number | undefined;
  label: string;
  uuid: string;
  isNew: boolean;
  isActive: boolean;
};

type NewIntelSateType = {
  subject: string;
  description: string;
  tags: TagStateType[];
  publishTo: PublishTo;
  clients: number[];
  locations: any[];
  sendEmailAlert: boolean;
  mediaLink: string;
};

const initialValues: NewIntelSateType = {
  subject: '',
  description: '',
  tags: [],
  publishTo: PublishTo.All,
  clients: [],
  locations: [],
  sendEmailAlert: true,
  mediaLink: '',
};

type IntelCreationPayload = {
  Category: 'Agent Intel';
  Subject: string;
  Description: string;
  SendEmailAlert?: boolean;
  PublishAllClients?: boolean;
  ActivityTags?: string[];
  ActivityClients?: number[];
  ActivityMeta?: any;
  Locations?: any[];
  Images?: {
    Content: string;
    Filename: string;
  }[];
};

export const ShareIntelModal = (props: ShareIntelModalProps) => {
  const { open, onClose, systemTags, allClients, loading, handleNewPost } = props;
  const [editor, setEditor] = useState<Editor | undefined>(Editor.Text);
  const [photos, setPhotos] = useState<any[]>([]);
  const [posting, setPosting] = useState<boolean>(false);

  useEffect(() => {
    if (!open) resetAllStates();
  }, [open]);

  const formik = useFormik({
    initialValues,
    onSubmit: (values) => {
      // Do nothing
    },
  });

  const resetAllStates = () => {
    setEditor(Editor.Text);
    setPhotos([]);
    formik.resetForm();
  };

  const handleEditorClick = (nextEditor: Editor) => () => {
    if (nextEditor === editor) {
      return;
    }

    setEditor(nextEditor);
  };

  const getTagsHeader = () => {
    const tagsArray = formik.values.tags;
    if (!tagsArray.length) return 'Tags';

    if (tagsArray.length > 2) {
      const firstTwo = tagsArray.slice(0, 2);
      const remainingItems = tagsArray.slice(2);
      const countRemaining = remainingItems.length;
      const firstTwoString = firstTwo.map((item) => item.label).join(', ');

      return `${firstTwoString}, ${countRemaining} more`;
    }

    const tagsStringified = tagsArray.map((item) => item.label).join(', ');
    return tagsStringified;
  };

  const getSelectTagLabel = (value) => {
    const found = systemTags.find((item) => item.Id === value);
    return found?.Title ?? '';
  };

  const handleTagSelection = (val) => {
    const isNumber = typeof val === 'number' && !Number.isNaN(val);

    const obj = {
      value: isNumber ? val : undefined,
      label: isNumber ? getSelectTagLabel(val) : val,
      uuid: uuid(),
      isNew: !isNumber,
      isActive: true,
    };

    const exists = formik.values.tags.find((item) =>
      isNumber ? item.value === val : item.label === val,
    );

    if (exists) {
      showErrorMessage('Tag already added');
      return;
    }

    const newTags = [...formik.values.tags, obj];
    formik.setFieldValue('tags', newTags);
  };

  const getClientsOptions = () => {
    const options = allClients
      .filter((client) => client.IsActive && client.InvitationStatus !== 'Pending')
      .map((client) => ({
        value: client.Id,
        label: `${client.FirstName}${client.LastName ? ` ${client.LastName}` : ''}`,
      }));

    return options;
  };

  const handleClientSelection = (val) => {
    formik.setFieldValue('clients', val?.length ? val : []);
  };

  const updateLocation = (result, prepareData) => {
    let updatedData = formik.values.locations.length ? [...formik.values.locations] : undefined;

    let areasOfOperation = prepareData?.length ? prepareData : updatedData || [];
    if (result?.deleteItemId) {
      areasOfOperation = areasOfOperation.filter(
        ({ ProviderPlaceId: itemId }) => itemId !== result.deleteItemId,
      );
    }
    areasOfOperation = uniqBy(areasOfOperation, 'ProviderPlaceId');
    areasOfOperation.sort(sortLocations);
    updatedData = [...areasOfOperation];

    if (updatedData) formik.setFieldValue('locations', updatedData?.length ? updatedData : []);
  };

  const isButtonDisable = () => {
    if (posting) return true;

    if (!formik.values.subject || !formik.values.description) return true;

    if (formik.values.mediaLink && !isValidURL(formik.values.mediaLink)) return true;

    if (formik.values.publishTo === PublishTo.Clients && !formik.values.clients.length) return true;

    if (formik.values.publishTo === PublishTo.Locations && !formik.values.locations.length)
      return true;

    return false;
  };

  const getProcessedPhotos = async () => {
    const results = await Promise.all(
      photos.map((photo) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = () => {
            resolve({
              Filename: photo.name,
              Content: (reader.result as string)?.replace(/^data:image\/[^;]+;base64,/, ''),
            });
          };

          reader.onerror = () => reject(new Error('Failed to read file'));

          reader.readAsDataURL(photo.originFileObj);
        });
      }),
    );

    return results;
  };

  const convertStateToPayloadObject = async (): Promise<IntelCreationPayload> => {
    let processedPhotos: any[] = [];
    if (photos.length) processedPhotos = await getProcessedPhotos();
    const obj: IntelCreationPayload = {
      Category: 'Agent Intel',
      Subject: formik.values.subject,
      Description: formik.values.description,
      SendEmailAlert: formik.values.sendEmailAlert,
      ActivityTags: formik.values.tags.map((item) => item.label),
      ...(formik.values.publishTo === PublishTo.All && { PublishAllClients: true }),
      ...(formik.values.publishTo === PublishTo.Clients && {
        ActivityClients: formik.values.clients,
      }),
      ...(formik.values.publishTo === PublishTo.Locations && {
        Locations: formik.values.locations,
      }),
      ...(!!processedPhotos.length && { Images: processedPhotos }),
      ...(formik.values.mediaLink && { MediaLink: formik.values.mediaLink }),
    };

    return obj;
  };

  const handlePost = async () => {
    setPosting(true);
    try {
      const payload = await convertStateToPayloadObject();
      const response = await postAgentIntel(payload);
      if (response.status === 200) {
        if (response.data.data) handleNewPost(response.data.data);
        showSuccessMessage('Intel posted');
        onClose();
      }
    } catch (e) {
      showErrorMessage('Unable to post the intel.');
    }
    setPosting(false);
  };

  const handleMediaLinkChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue('mediaLink', value);
  };

  return (
    <Modal
      title={loading ? null : <div className={styles.modalTitle}>New Intel</div>}
      open={open}
      width={680}
      onCancel={onClose}
      onOk={onClose}
      cancelButtonProps={{ style: { display: 'none' } }}
      footer={null}
      className={styles.modal}
    >
      {loading ? (
        <Spinner />
      ) : (
        <>
          <div className={styles.modalContent}>
            {/*--------------->SUBJECT*/}
            <div
              className={classNames(styles.section, {
                [styles.sectionInactive]: editor !== Editor.Text,
              })}
              onClick={handleEditorClick(Editor.Text)}
            >
              {editor !== Editor.Text ? (
                <>
                  <div className={styles.icon}>
                    <DraftsIcon />
                  </div>
                  {formik.values.subject || formik.values.description ? (
                    <div className={styles.textContainer}>
                      <div className={styles.subject}>{formik.values.subject}</div>
                      <div className={styles.description}>{formik.values.description}</div>
                    </div>
                  ) : (
                    <div className={styles.text}>Add text</div>
                  )}
                </>
              ) : (
                <div className={styles.sectionEditor}>
                  <Input
                    className={styles.subjectInput}
                    hideOutline
                    placeholder="Subject..."
                    value={formik.values.subject}
                    onChange={(e) => formik.setFieldValue('subject', e.target.value)}
                  />
                  <div className={styles.divider} />
                  <textarea
                    className={styles.descriptionInput}
                    placeholder="Add text..."
                    rows={2}
                    value={formik.values.description}
                    onChange={(e) => formik.setFieldValue('description', e.target.value)}
                  />
                </div>
              )}
            </div>
            {/*--------------->PHOTOS*/}
            <div
              className={classNames(styles.section, {
                [styles.sectionInactive]: editor !== Editor.Photo,
              })}
              onClick={handleEditorClick(Editor.Photo)}
            >
              {editor !== Editor.Photo ? (
                <>
                  {photos.length ? (
                    <div className={styles.thumbnails}>
                      <img
                        src={photos[0].thumbUrl}
                        className={classNames(styles.thumbnail, {
                          [styles.thumbnailTilt]: photos.length > 1,
                        })}
                      />
                      {photos.length > 1 && (
                        <img
                          src={photos[1].thumbUrl}
                          className={classNames(styles.thumbnail, styles.thumbnailBelow)}
                          onChange={handleMediaLinkChange}
                        />
                      )}
                    </div>
                  ) : (
                    <div className={styles.icon}>
                      <FrameIcon />
                    </div>
                  )}
                  <div className={styles.text}>
                    {photos.length
                      ? `${photos.length} Photo${photos.length === 1 ? '' : 's'}`
                      : 'Photos & Videos'}
                  </div>
                </>
              ) : (
                <UploadedPhotoGallery
                  photos={photos}
                  setPhotos={setPhotos}
                  mediaLink={formik.values.mediaLink}
                  handleMediaLinkChange={handleMediaLinkChange}
                />
              )}
            </div>
            {/*--------------->TAGS*/}
            <div
              className={classNames(styles.section, {
                [styles.sectionInactive]: editor !== Editor.Tags,
              })}
              onClick={handleEditorClick(Editor.Tags)}
            >
              {editor !== Editor.Tags ? (
                <>
                  <div className={styles.icon}>
                    <TagsIcon />
                  </div>
                  <div className={styles.text}>{getTagsHeader()}</div>
                </>
              ) : (
                <div className={styles.sectionEditor}>
                  <div className={styles.sectionEditorTitle}>Tags</div>
                  <Autocomplete
                    ddOptions={systemTags.map((item) => ({
                      value: item.Id,
                      label: item.Title,
                    }))}
                    placeholder="Search tags..."
                    handleOptionsSelection={handleTagSelection}
                    useOptionsValues={
                      formik.values.tags.map((item) => item.value).filter(Boolean) as number[]
                    }
                    allowCreation
                  />
                  <div className={styles.tagWrapper}>
                    {formik.values.tags.map((tag, index) => (
                      <div
                        className={classNames(styles.tag, {
                          [styles.active]: tag.isActive,
                          [styles.deletable]: tag.isNew,
                        })}
                        key={`${tag.label}-${index}`}
                        onClick={() => {
                          const nextTags = formik.values.tags.map((t) =>
                            t.label === tag.label ? { ...t, isActive: !t.isActive } : t,
                          );

                          return formik.setFieldValue('tags', nextTags);
                        }}
                      >
                        <div>{tag.label}</div>
                        {tag.isNew && (
                          <CrossIcon
                            onClick={(event) => {
                              event.stopPropagation();
                              return formik.setFieldValue(
                                'tags',
                                formik.values.tags.filter((t) => t.label !== tag.label),
                              );
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/*--------------->PUBLISH*/}
            <div
              className={classNames(styles.section, {
                [styles.sectionInactive]: editor !== Editor.PublishTo,
              })}
              onClick={handleEditorClick(Editor.PublishTo)}
            >
              {editor !== Editor.PublishTo ? (
                <>
                  <div className={styles.icon}>
                    <UsersIcon />
                  </div>
                  <div className={styles.text}>Publish to all clients</div>
                </>
              ) : (
                <div className={styles.sectionEditor}>
                  <div className={styles.sectionEditorTitle}>Publish</div>
                  <div>
                    <div
                      className={classNames(styles.toggleOption, {
                        [styles.selected]: formik.values.publishTo === PublishTo.All,
                      })}
                      onClick={() => formik.setFieldValue('publishTo', PublishTo.All)}
                    >
                      <div className={styles.circle}>
                        {formik.values.publishTo === PublishTo.All && <CheckmarkIcon />}
                      </div>
                      All Clients
                    </div>
                  </div>

                  <div>
                    <div
                      className={classNames(styles.toggleOption, {
                        [styles.selected]: formik.values.publishTo === PublishTo.Clients,
                      })}
                      onClick={() => formik.setFieldValue('publishTo', PublishTo.Clients)}
                    >
                      <div className={styles.circle}>
                        {formik.values.publishTo === PublishTo.Clients && <CheckmarkIcon />}
                      </div>
                      Specific Clients
                    </div>

                    {formik.values.publishTo === PublishTo.Clients && (
                      <div className={classNames(styles.searchWrapper, styles.searchWrapperClient)}>
                        <SearchIcon />
                        <Select
                          mode="multiple"
                          style={{ width: '100%' }}
                          placeholder="Select clients..."
                          onChange={handleClientSelection}
                          options={getClientsOptions()}
                          className={styles.clientSelector}
                          popupClassName={styles.clientSelectorPopup}
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <div
                      className={classNames(styles.toggleOption, {
                        [styles.selected]: formik.values.publishTo === PublishTo.Locations,
                      })}
                      onClick={() => formik.setFieldValue('publishTo', PublishTo.Locations)}
                    >
                      <div className={styles.circle}>
                        {formik.values.publishTo === PublishTo.Locations && <CheckmarkIcon />}
                      </div>
                      By Location
                    </div>

                    {formik.values.publishTo === PublishTo.Locations && (
                      <div className={styles.searchWrapper}>
                        <Locations
                          multiple
                          allowStates={true}
                          getStates={true}
                          onResult={(result, prepareData) => updateLocation(result, prepareData)}
                          value={
                            formik.values.locations.length ? formik.values.locations : undefined
                          }
                          placeholderIcon={<ServiceIcons icon={ServiceIconTypes.location} />}
                          placeholder={'Enter locations...'}
                          activeInputClassName={styles.activeInputClassName}
                          valuesWrapperClassName={styles.valuesWrapperClassName}
                          placeholderClassName={styles.placeholderClass}
                          valuesContainerClassName={styles.valuesContainer}
                          dropdownClassName={styles.locationDropdown}
                          searchIcon={!!formik.values.locations.length}
                          variant={Locations.ROUND}
                          shouldFilterStateLevelResults={false}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/*--------------->EMAIL*/}
            <div className={styles.confirmEmail}>
              <Switch
                className={styles.switch}
                id="sendEmailAlert"
                checked={formik.values.sendEmailAlert}
                onChange={(checked) => formik.setFieldValue('sendEmailAlert', checked)}
              />
              <label className={styles.label} htmlFor="sendEmailAlert">
                Send Email Alert
              </label>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <Button
              variant="secondary"
              onClick={handlePost}
              className={styles.modalPostButton}
              disabled={isButtonDisable()}
              loading={posting}
            >
              Post
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

const UploadedPhotoGallery = ({ photos, setPhotos, mediaLink, handleMediaLinkChange }) => {
  const [thumbnail, setThumbnail] = useState<string>('');
  const maxPhotos = 10;

  const handleFilesInput = ({ fileList: newFileList }) => {
    if (newFileList.length > maxPhotos) {
      showErrorMessage('You can upload a maximum of 10 photos.');
      return;
    }

    setPhotos(newFileList);
  };

  const getHeaderText = () => {
    if (!photos.length) return 'Photos & Videos';

    if (photos.length === 1) return '1 Photo';

    return `${photos.length} Photos`;
  };

  const beforeUpload = (file, fileList: any[]) => {
    const areAllImages = fileList.every((item) => item.type.startsWith('image/'));

    if (!areAllImages) {
      showErrorMessage('Only images are allowed.');
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const processThumbnail = () => {
    const thumbnailUrl = getYouTubeThumbnail(mediaLink);

    setThumbnail(thumbnailUrl ?? '');
  };

  const customItemRender = (originNode, file, currFileList, actions) => {
    return (
      <div style={{ position: 'relative' }}>
        {originNode}
        <div
          className={styles.cross}
          onClick={() => actions.remove()}
          style={{
            position: 'absolute',
            height: 24,
            width: 24,
            backgroundColor: '#fff',
            cursor: 'pointer',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            top: 6,
            right: 6,
          }}
        >
          <CrossIcon />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.gallery}>
      <div className={styles.header}>{getHeaderText()}</div>
      <div className={styles.content}>
        <div className={styles.mediaLinkContainer}>
          <SearchIcon />
          <input
            type="text"
            placeholder="Paste YouTube or media URL..."
            className={styles.mediaLink}
            value={mediaLink}
            onChange={handleMediaLinkChange}
            onBlur={() => {
              if (mediaLink && !isValidURL(mediaLink)) {
                const e = {
                  target: {
                    value: '',
                  },
                };
                handleMediaLinkChange(e);
                showErrorMessage('Please provide a valid media link.');
                return;
              }

              processThumbnail();
            }}
          />
        </div>
        <div className={styles.imagesContainer}>
          {thumbnail ? (
            <div className={styles.thumbnailContainer}>
              <img src={thumbnail} alt="Thumbnail" className={styles.thumbnail} />
              <div
                className={styles.cross}
                onClick={() => {
                  const e = {
                    target: {
                      value: '',
                    },
                  };
                  handleMediaLinkChange(e);
                  setThumbnail('');
                }}
              >
                <CrossIcon />
              </div>
            </div>
          ) : null}
          <Upload
            listType="picture-card"
            fileList={photos}
            onChange={handleFilesInput}
            beforeUpload={beforeUpload}
            onPreview={() => false}
            accept="image/*"
            multiple
            className={thumbnail ? styles.uploader : undefined}
            itemRender={customItemRender}
            showUploadList={{
              showRemoveIcon: false,
            }}
          >
            {photos.length >= maxPhotos ? null : (
              <div>
                <UploadIcon />
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '22px',
                    letterSpacing: -0.18,
                    color: '#262626',
                  }}
                >
                  Upload
                </div>
              </div>
            )}
          </Upload>
        </div>
      </div>
    </div>
  );
};
